import { adminApi } from '@/config/axios.config';
import {
  AutomaticInvoice,
  AutomaticInvoiceFilters,
  AutomaticInvoiceStats,
} from '../types/admin.types';
import type { CurrentMonthScheduledInvoices } from '../types/admin.types';

export const automaticInvoicesApiService = {
  /**
   * Obtiene todas las facturas automáticas
   */
  getAllAutomaticInvoices: async (
    filters?: AutomaticInvoiceFilters,
  ): Promise<AutomaticInvoice[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.tenantId) params.append('tenantId', filters.tenantId);
      if (filters?.planId) params.append('planId', filters.planId);
      if (filters?.frequency) params.append('frequency', filters.frequency);
      if (filters?.isActive !== undefined)
        params.append('isActive', String(filters.isActive));
      // Por defecto, filtrar por mes actual (currentMonth=true)
      if (filters?.currentMonth !== undefined) {
        params.append('currentMonth', String(filters.currentMonth));
      } else {
        params.append('currentMonth', 'true'); // Por defecto true
      }

      const response = await adminApi.get('/billing/automatic-invoices', {
        params,
      });
      return response.data?.data || response.data || [];
    } catch (error) {
      return [];
    }
  },

  /**
   * Obtiene una factura automática por ID
   */
  getAutomaticInvoice: async (id: string): Promise<AutomaticInvoice> => {
    const response = await adminApi.get(`/billing/automatic-invoices/${id}`);
    return response.data?.data || response.data;
  },

  /**
   * Obtiene las estadísticas de facturas automáticas
   */
  getAutomaticInvoiceStats: async (): Promise<AutomaticInvoiceStats> => {
    try {
      const response = await adminApi.get(
        '/billing/automatic-invoices/stats',
      );
      return (
        response.data?.data ||
        response.data || {
          total: 0,
          active: 0,
          inactive: 0,
          monthly: 0,
          yearly: 0,
          totalAmount: 0,
        }
      );
    } catch (error) {
      return {
        total: 0,
        active: 0,
        inactive: 0,
        monthly: 0,
        yearly: 0,
        totalAmount: 0,
      };
    }
  },

  /**
   * Activa/desactiva una factura automática
   */
  toggleAutomaticInvoice: async (
    id: string,
  ): Promise<AutomaticInvoice> => {
    const response = await adminApi.patch(
      `/billing/automatic-invoices/${id}/toggle`,
    );
    return response.data?.data || response.data;
  },

  /**
   * Actualiza el día del mes para facturas mensuales
   */
  updateDayOfMonth: async (
    id: string,
    dayOfMonth: number,
  ): Promise<AutomaticInvoice> => {
    const response = await adminApi.patch(
      `/billing/automatic-invoices/${id}/day-of-month`,
      {
        dayOfMonth,
      },
    );
    return response.data?.data || response.data;
  },

  /**
   * Obtiene las facturas programadas para el mes actual
   */
  getCurrentMonthScheduledInvoices: async (): Promise<CurrentMonthScheduledInvoices> => {
    const response = await adminApi.get('/billing/automatic-invoices/current-month');
    return response.data?.data || response.data;
  },

  /**
   * Genera facturas para una fecha específica (testing)
   */
  generateInvoicesForTestDate: async (testDate: string): Promise<any> => {
    const response = await adminApi.post('/billing/automatic-invoices/generate/test', {
      testDate,
    });
    return response.data?.data || response.data;
  },

  /**
   * Genera facturas para hoy (manual)
   */
  generateInvoicesManually: async (): Promise<any> => {
    const response = await adminApi.post('/billing/automatic-invoices/generate');
    return response.data?.data || response.data;
  },
};

